/**
 * Story header with editable title and status badge
 * Story 199: View story details with PRD and design
 * Story 284: Web UI override of QA→Done status (Mark Done button only)
 * Story 288: View QA evidence for completed stories
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useUpdateStoryTitle } from '../../hooks/useStoryMutations';
import type { StoryDetail } from '../../hooks/useStory';
import { ThemeSwitcher } from '../ThemeSwitcher';
import { ForceCompleteDialog } from './ForceCompleteDialog';
import { QAEvidenceModal } from './QAEvidenceModal';
import { ScalesIcon } from '../icons/ScalesIcon';
import './StoryHeader.css';

interface StoryHeaderProps {
  story: StoryDetail;
  projectSlug: string;
}

/** Check if story has non-empty QA evidence */
function hasQAEvidence(evidence: Record<string, string> | undefined): boolean {
  if (!evidence) return false;
  return Object.keys(evidence).length > 0;
}

export function StoryHeader({ story, projectSlug }: StoryHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(story.title);
  const [showForceCompleteDialog, setShowForceCompleteDialog] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateMutation = useUpdateStoryTitle();

  // Sync title when story changes
  useEffect(() => {
    setTitle(story.title);
  }, [story.title]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== story.title) {
      updateMutation.mutate({
        projectSlug,
        storyExternalId: story.external_id,
        title: trimmed,
      });
    }
    setIsEditing(false);
  }, [title, story.title, story.external_id, projectSlug, updateMutation]);

  const handleCancel = useCallback(() => {
    setTitle(story.title);
    setIsEditing(false);
  }, [story.title]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  return (
    <div className="story-header">
      {isEditing ? (
        <input
          ref={inputRef}
          className="story-header__input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
        />
      ) : (
        <h1
          className="story-header__title"
          onClick={() => setIsEditing(true)}
          title="Click to edit title"
        >
          Story {story.external_id}: {story.title}
        </h1>
      )}
      <div className="story-header__meta">
        <span className={`story-header__badge story-header__badge--${story.status}`}>
          {story.status}
        </span>
        {/* Story 288: Evidence button for completed stories with QA evidence */}
        {story.status === 'done' && hasQAEvidence(story.qa_evidence) && (
          <button
            className="story-header__evidence"
            onClick={() => setShowEvidenceModal(true)}
            title="View QA evidence"
          >
            <ScalesIcon size={14} />
            Evidence
          </button>
        )}
        {/* Story 284: Mark Done button for qa status */}
        {story.status === 'qa' && (
          <button
            className="story-header__force-done"
            onClick={() => setShowForceCompleteDialog(true)}
          >
            Mark Done...
          </button>
        )}
        {story.epic_slug && (
          <span className="story-header__epic">{story.epic_slug}</span>
        )}
        {/* Story 564: project settings moved to the story list header, which
            renders whether or not a story is open. */}
        <ThemeSwitcher />
      </div>
      {/* Story 284: Force complete confirmation dialog */}
      <ForceCompleteDialog
        isOpen={showForceCompleteDialog}
        onClose={() => setShowForceCompleteDialog(false)}
        projectSlug={projectSlug}
        storyExternalId={story.external_id}
      />
      {/* Story 288: QA evidence modal */}
      <QAEvidenceModal
        isOpen={showEvidenceModal}
        onClose={() => setShowEvidenceModal(false)}
        evidence={story.qa_evidence || {}}
      />
    </div>
  );
}
